import './NotificationsEditableContent.css';

import React, { useRef, useState } from 'react';

import { RoutalSelect, RoutalSelectMultiSelectOption } from '@nx-smartmonkey/ui';
import Text from '../../../react-components/Text';

const SUBSTITUTION_CHARACTER_START = `{{`;
const SUBSTITUTION_CHARACTER_END = `}}`;

export interface ISubstitution {
  id: string;
  label: string;
}

const baseToHtml = (text: string, substitutions: ISubstitution[]) => {
  const regexp = new RegExp(`${SUBSTITUTION_CHARACTER_START}.+?${SUBSTITUTION_CHARACTER_END}`, `g`);
  const matches = text.match(regexp);

  let parsedBody = text.replace(/\n/g, `<br>`);

  (matches ?? []).forEach((match: any) => {
    const cleanMatchText = match.replace(SUBSTITUTION_CHARACTER_START, ``).replace(SUBSTITUTION_CHARACTER_END, ``);

    if (substitutions.map((sub) => sub.id).includes(cleanMatchText)) {
      const substitution = substitutions.find((sub) => sub.id === cleanMatchText);
      if (substitution) {
        parsedBody = parsedBody.replace(
          match,
          `<span class="substitution-pill" contenteditable="false">${substitution.label}</span>`
        );
      }
    }
  });

  return parsedBody;
};

const htmlToBase = (htmlCode: string, substitutions: ISubstitution[]) => {
  const matches = htmlCode.match(/<span class="substitution-pill" contenteditable="false">.+?<\/span>/g);

  let parsedBody = htmlCode.replace(/<br>/g, `\n`).replace(/&nbsp;/g, ` `);

  (matches ?? []).forEach((match: any) => {
    const cleanMatchText = match
      .replace(`<span class="substitution-pill" contenteditable="false">`, ``)
      .replace(`</span>`, ``);

    if (substitutions.map((sub) => sub.label).includes(cleanMatchText)) {
      const substitution = substitutions.find((sub) => sub.label === cleanMatchText);
      if (substitution) {
        parsedBody = parsedBody.replace(
          `<span class="substitution-pill" contenteditable="false">${cleanMatchText}</span>`,
          `${SUBSTITUTION_CHARACTER_START}${substitution.id}${SUBSTITUTION_CHARACTER_END}`
        );
      }
    }
  });

  return parsedBody;
};

interface NotificationsEditableContentProps {
  id: string;
  value?: string;
  limit?: number;
  substitutions: ISubstitution[];
  inputStyle: React.CSSProperties;
  placeholder?: string;
  footerContent?: React.ReactElement;
  visibleSubstitutions: boolean;
  setVisibleSubstitutions: (value: boolean) => any;
  onValueChange: (value: string) => any;
}

export const NotificationsEditableContent = ({
  id,
  value,
  limit,
  substitutions,
  inputStyle,
  placeholder,
  footerContent,
  visibleSubstitutions,
  setVisibleSubstitutions,
  onValueChange,
}: NotificationsEditableContentProps) => {
  const [range, setRange] = useState<Range | undefined>(undefined);

  const input = useRef() as React.MutableRefObject<HTMLInputElement>;
  const parsedHTML = baseToHtml(value ?? ``, substitutions);
  const html = useRef(parsedHTML) as React.MutableRefObject<string>;

  const innerOnValueChange = (text: any) => {
    // REMOVING POTENTIALLY DANGEROUS CODE
    input.current.childNodes.forEach((node: any) => {
      if ([`SPAN`, `#text`, `BR`].indexOf(node.nodeName) === -1) {
        node.remove();
      }
      if (node.nodeName === `SPAN` && !(node.getAttribute(`class`) === `substitution-pill`)) {
        node.remove();
      }
    });

    const newValue = htmlToBase(text, substitutions);

    // CALL UPPER FUNCTION
    onValueChange(newValue);
  };

  const refreshRange = () => {
    const selection = window.getSelection();
    if (!selection) return;

    if (
      selection.anchorNode &&
      ((selection.anchorNode as HTMLElement).id === id ||
        ((selection.anchorNode.parentNode as HTMLElement) &&
          (selection.anchorNode.parentNode as HTMLElement).id === id))
    ) {
      const selectionRange = selection.getRangeAt(0);
      const clonedRange = selectionRange.cloneRange();
      setRange(clonedRange);
    }
  };

  const isEnabled = () => {
    return !limit || !input?.current?.textContent || input.current.textContent.length < limit;
  };

  return (
    <>
      <div
        id={id}
        ref={input}
        contentEditable
        onPaste={(evt: any) => {
          const pastedText = evt.clipboardData.getData(`text/plain`);
          if (limit && pastedText.length > limit) {
            evt.preventDefault();
          }
        }}
        onKeyDown={(evt: any) => {
          if (
            !isEnabled() &&
            // Backspace, Delete and arrows
            ![8, 46, 33, 34, 35, 36, 37, 38, 39, 40].includes(evt.keyCode)
          ) {
            evt.preventDefault();
          }
        }}
        onKeyUp={(evt: any) => {
          refreshRange();
        }}
        onClick={(e: any) => {
          e.stopPropagation();
          setVisibleSubstitutions(true);
          refreshRange();
        }}
        onMouseEnter={() => {
          setVisibleSubstitutions(true);
        }}
        onInput={(evt: any) => {
          innerOnValueChange(evt.target.innerHTML);
        }}
        style={inputStyle}
        // @ts-expect-error
        placeholder={placeholder}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: html.current }}
      />

      {footerContent}

      <RoutalSelect
        id="substitutions-select-button-popover"
        hideArrow
        // style={{
        //   position: `absolute`,
        //   width: `calc(34px - 12px)`,
        //   right: `-38px`,
        //   top: 0,
        //   padding: `4px 6px`,
        //   margin: 0,
        //   height: `unset`,
        //   borderColor: RoutalPalette.neutral10,
        //   backgroundColor: RoutalPalette.neutral00,
        //   display: visibleSubstitutions ? `block` : `none`,
        // }}
        style={{
          position: `absolute`,
          right: `-30px`,
          top: 0,
          height: `unset`,
          width: `26px`,
        }}
        popoverProps={{
          maxHeight: `500px`,
          options: [
            ...substitutions.map((substitution, index: number): RoutalSelectMultiSelectOption => {
              return {
                key: `available-pod-option-${substitution.id}-${index}`,
                content: <Text style={{ cursor: `pointer` }}>{substitution.label}</Text>,
                listItemStyles: {
                  maxWidth: `200px`,
                },
                onClick: () => {
                  if (isEnabled()) {
                    const span = document.createElement(`span`);
                    span.className = `substitution-pill`;
                    span.contentEditable = `false`;
                    span.textContent = substitution.label;

                    if (!range) return;

                    range.insertNode(document.createTextNode(`\u00a0`));

                    range.insertNode(span);

                    range.insertNode(document.createTextNode(`\u00a0`));

                    range.setStart(range.endContainer, range.endOffset);

                    const parentNode = document.getElementById(id);
                    parentNode?.focus({ preventScroll: false });

                    range.collapse();

                    innerOnValueChange(baseToHtml(input.current.innerHTML, substitutions));

                    setVisibleSubstitutions(false);
                  }
                },
              };
            }),
          ],
        }}
      >
        <Text style={{ cursor: `pointer` }}>{`{...}`}</Text>
      </RoutalSelect>
    </>
  );
};
